import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import LeafSignature from "@/components/heritage/LeafSignature";
import imgMorning from "@/assets/flauriel-island-bush-tea.png";
import imgHair from "@/assets/yaphene-hair-care-display.jpg";
import imgEvening from "@/assets/calume-ceramic-candle.jpg";
import imgKitchen from "@/assets/chlm-daily-ritual.jpg";
import imgReset from "@/assets/anaelli-life-balance-vol1.jpg";
import imgRooted from "@/assets/forest/rooted-ritual-hero.jpg";

type Ritual = {
  title: string;
  body: string;
  includes: string[];
  image: string;
  to: string;
};

export const RITUALS: Ritual[] = [
  {
    title: "Rooted Ritual Journey",
    body: "A guided digital ritual combining music, reflection, journaling and wellness practices inspired by Caribbean wisdom and the natural rhythms of life.",
    includes: ["Music", "Reflection", "Journaling", "Wellness practices"],
    image: imgRooted,
    to: "/rooted-ritual",
  },
  {
    title: "Caribbean Morning Ritual",
    body: "A slow, sunlit start. Tea steeping, sunlight on skin, a few written lines before the day begins.",
    includes: ["Bush tea", "Sunlight", "Reflection journal", "Botanical tonic"],
    image: imgMorning,
    to: "/caribbean-heritage-longevity-method",
  },
  {
    title: "Island Hair Ritual",
    body: "A wash-day tradition passed through generations — scalp tonic, oil, intention.",
    includes: ["Hair oil", "Scalp tonic", "Wash day guide"],
    image: imgHair,
    to: "/brands/yaphene",
  },
  {
    title: "Evening Restoration",
    body: "A quiet close to the day. Warm bath, candlelight, the last cup of tea.",
    includes: ["Bath soak", "Candle", "Tea", "Reflection journal"],
    image: imgEvening,
    to: "/brands/calume",
  },
  {
    title: "Longevity Kitchen",
    body: "Caribbean wellness from the pot — herbal foods, traditional recipes, the longevity plate.",
    includes: ["Bush teas", "Herbal foods", "Heritage recipes"],
    image: imgKitchen,
    to: "/caribbean-heritage-longevity-method",
  },
  {
    title: "Sunday Reset Ritual",
    body: "The weekly reset — body care, planning, gentle emotional housekeeping.",
    includes: ["Body care", "Wellness planner", "Emotional reset tools"],
    image: imgReset,
    to: "/brands/anaelli",
  },
];

const SignatureRituals = () => {
  return (
    <section
      className="relative overflow-hidden py-24 md:py-32"
      style={{
        background:
          "linear-gradient(180deg, hsl(40 30% 95%) 0%, hsl(40 35% 92%) 100%)",
      }}
    >
      <LeafSignature variant="watermark" position="top-right" size="xl" tone="light" />
      <LeafSignature variant="watermark" position="bottom-left" size="lg" tone="light" />
      <div className="relative container mx-auto px-6" style={{ maxWidth: "1400px" }}>
        <Reveal className="max-w-2xl mb-16 md:mb-20">
          <p className="font-sans text-[11px] tracking-[0.42em] uppercase text-accent mb-5">
            Lived Traditions
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-foreground leading-[1.05] mb-6">
            Signature Rituals
          </h2>
          <p className="font-sans text-base md:text-lg text-foreground/70 leading-relaxed">
            Objects are only part of the practice. Each ritual weaves botanicals,
            sensory atmosphere and Caribbean rhythm into a way of living.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {RITUALS.map((r, idx) => (
            <Reveal key={r.title} delay={idx * 80}>
              <Link to={r.to} className="group block">
                <div className="relative aspect-[5/6] overflow-hidden rounded-sm mb-6 bg-[hsl(150_35%_8%)]">
                  <img
                    src={r.image}
                    alt={r.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1100ms] ease-out"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-t from-[hsl(150_35%_8%)]/40 via-transparent to-transparent"
                  />
                </div>
                <h3 className="font-serif text-2xl text-foreground mb-3 group-hover:text-accent transition-colors duration-500">
                  {r.title}
                </h3>
                <p className="font-sans text-foreground/70 leading-relaxed mb-4 text-[15px]">
                  {r.body}
                </p>
                <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-foreground/55">
                  {r.includes.join(" · ")}
                </p>
                <span className="mt-5 inline-block h-px w-10 bg-accent/70 group-hover:w-28 transition-all duration-500" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SignatureRituals;
